import React, { useState, useEffect } from 'react';
import axios from "../../../../axios-order";
import { Snackbar, Alert } from '@mui/material';

function Nine({ handleBack, updateTableData, data, reasonsData, title, get_gstr9_report_data }) {
  const [reasonText, setReasonText] = useState('');
  const [inputValues, setInputValues] = useState(
    data?.map(item => ({
      taxable_non_taxable_value: item.taxable_non_taxable_value?.toString() ?? '',
      central_tax: item.central_tax?.toString() ?? '',
      state_tax_ut_tax: item.state_tax_ut_tax?.toString() ?? '',
      integrated_tax: item.integrated_tax?.toString() ?? '',
      cess: item.cess?.toString() ?? '',
      taxable_and_non_taxable_manual: item.taxable_and_non_taxable_manual?.toString() ?? '',
      central_tax_manual: item.central_tax_manual?.toString() ?? '',
      state_tax_ut_tax_manual: item.state_tax_ut_tax_manual?.toString() ?? '',
      integrated_tax_manual: item.integrated_tax_manual?.toString() ?? '',
      cess_manual: item.cess_manual?.toString() ?? ''
    })) || []
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (reasonsData && reasonsData.length > 0 && reasonsData[0].reasons) {
      setReasonText(reasonsData[0].reasons);
    }
  }, [reasonsData]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  // Fixed: Improved change handler with proper negative support
  const handleManualInputChange = (index, field, value) => {
    let clean = value.replace('₹', '').trim();

    if (clean === '') {
      const newInputValues = [...inputValues];
      newInputValues[index] = { ...newInputValues[index], [field]: '' };
      setInputValues(newInputValues);

      const newData = [...data];
      newData[index] = { ...newData[index], [field]: null };
      updateTableData?.(newData);
      return;
    }

    // Allow only one minus at start
    if (clean.includes('-')) {
      clean = '-' + clean.replace(/-/g, '');
    }

    // Validate: optional minus, digits, optional decimal up to 2 places
    if (/^-?\d*\.?\d{0,2}$/.test(clean)) {
      const newInputValues = [...inputValues];
      newInputValues[index] = { ...newInputValues[index], [field]: clean };
      setInputValues(newInputValues);

      const parsed = parseFloat(clean);
      const newData = [...data];
      newData[index] = { ...newData[index], [field]: isNaN(parsed) ? null : parsed };
      updateTableData?.(newData);
    }
  };

  // Fixed: Proper keydown handler for minus sign
  const handleKeyDown = (e) => {
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '.'];

    if (allowedKeys.includes(e.key)) return;

    const input = e.target;
    const rawValue = input.value;
    const cleanValue = rawValue.replace('₹', '').trim();
    const cursorPos = input.selectionStart;

    // Allow only one decimal
    if (e.key === '.') {
      if (cleanValue.includes('.')) e.preventDefault();
      return;
    }

    // Allow minus only at start (position 0 or 1) and only once
    if (e.key === '-') {
      const isAtStart = cursorPos === 0 || cursorPos === 1;
      const hasMinus = cleanValue.startsWith('-');

      if (isAtStart && !hasMinus) {
        return; // allow minus
      } else {
        e.preventDefault();
      }
      return;
    }

    e.preventDefault();
  };

  // Helper to show ₹0.00 for empty/zero (your original behavior)
  const getDisplayValue = (rawValue) => {
    if (rawValue === '' || rawValue == null) return '';
    return `₹${rawValue}`;
  };

  const handleSubmit = async () => {
    const payload = {
      business_entity_id: localStorage.getItem("business_entity_id"),
      gstr9c_recon_id: localStorage.getItem("gstr9c_recon_id"),
      records: {
        "9.Reconciliation of rate wise liability and amount payable thereon": data.map((item, index) => ({
          id: item.id,
          gstr9c_recon_id: item.gstr9c_recon_id,
          business_entity_id: item.business_entity_id,
          description: item.description,
          taxable_non_taxable_value: inputValues[index].taxable_non_taxable_value === '' ? null : parseFloat(inputValues[index].taxable_non_taxable_value),
          central_tax: inputValues[index].central_tax === '' ? null : parseFloat(inputValues[index].central_tax),
          state_tax_ut_tax: inputValues[index].state_tax_ut_tax === '' ? null : parseFloat(inputValues[index].state_tax_ut_tax),
          integrated_tax: inputValues[index].integrated_tax === '' ? null : parseFloat(inputValues[index].integrated_tax),
          cess: inputValues[index].cess === '' ? null : parseFloat(inputValues[index].cess),
          amount_of_itc_total: item.amount_of_itc_total || null,
          amount_of_itc_eligible: item.amount_of_itc_eligible || null,
          taxable_and_non_taxable_manual: inputValues[index].taxable_and_non_taxable_manual === '' ? null : parseFloat(inputValues[index].taxable_and_non_taxable_manual),
          central_tax_manual: inputValues[index].central_tax_manual === '' ? null : parseFloat(inputValues[index].central_tax_manual),
          state_tax_ut_tax_manual: inputValues[index].state_tax_ut_tax_manual === '' ? null : parseFloat(inputValues[index].state_tax_ut_tax_manual),
          integrated_tax_manual: inputValues[index].integrated_tax_manual === '' ? null : parseFloat(inputValues[index].integrated_tax_manual),
          cess_manual: inputValues[index].cess_manual === '' ? null : parseFloat(inputValues[index].cess_manual)
        })),
        "10.Reasons for Un - Reconciled Payment of Tax": [
          {
            description: "Reasons for Un - Reconciled Payment of Tax",
            reasons: reasonText === '' ? null : reasonText
          }
        ]
      }
    };

    try {
      const response = await axios.post("/gstr9c_manual_data_update", payload);
      if (response.data.status === true) {
        setSnackbar({
          open: true,
          message: response.data.message || 'Values updated successfully.',
          severity: 'success',
        });
        if (typeof get_gstr9_report_data === 'function') {
          await get_gstr9_report_data();
        }
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || 'Failed to update values.',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Error submitting data:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while submitting data.',
        severity: 'error',
      });
    }
  };

  const hiddenColumnsDescriptions = [
    'K2.Supplies on which e-commerce operator is required to pay tax as per section 9(5) (including amendments, if any) [E-commerce operator to report]',
    'L.Interest',
    'M.Late Fee',
    'N.Penalty',
    'O.Others',
    'P.Total amount to be paid as per tables above (A to O)*',
    'Q.Total amount paid as declared in Annual Return (GSTR 9)*',
    'R.Un-reconciled payment (Q-P)*'
  ];

  const inputStyle = {
    color: '#1e293b',
    fontWeight: '500',
    fontSize: '12px',
    fontFamily: '"Roboto Mono", monospace',
    textAlign: 'right',
    padding: '6px',
    border: '1px solid #e2e8f0',
    backgroundColor: 'white',
    borderRadius: '4px',
    outline: 'none',
    width: '100%',
    height: '32px',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '80px',
    minHeight: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  };

  const inputFocusStyle = {
    borderColor: '#88d8a3',
    boxShadow: '0 0 0 2px rgba(136, 216, 163, 0.1)'
  };

  const inputBlurStyle = {
    borderColor: '#e2e8f0',
    boxShadow: 'none'
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '30px', width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
        <button
          onClick={handleBack}
          style={{
            marginBottom: '20px',
            padding: '8px 16px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#64748b'
          }}
        >
          Back
        </button>

        <h2 style={{ marginBottom: '20px', color: '#1e293b', fontSize: '20px', fontWeight: '700', textAlign: 'center' }}>
          {title || 'Table 9 - Details'}
        </h2>

        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, minmax(80px, 1fr))',
          gap: '8px',
          fontWeight: '600',
          color: '#475569',
          padding: '0',
          borderBottom: '2px solid #e2e8f0',
          fontSize: '14px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          marginBottom: '8px',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          minHeight: '50px',
          alignItems: 'stretch'
        }}>
          <div style={{ textAlign: 'center', padding: '12px 4px', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', boxSizing: 'border-box' }}>S.No.</div>
          <div style={{ textAlign: 'center', padding: '12px 4px', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', boxSizing: 'border-box' }}>Description</div>
          <div style={{ textAlign: 'center', padding: '12px 4px', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', boxSizing: 'border-box' }}>Taxable Value (₹)</div>
          <div style={{ textAlign: 'center', gridColumn: '4 / span 4', padding: '12px 4px', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', boxSizing: 'border-box' }}>Tax payable (₹)</div>
          <div style={{ textAlign: 'center', padding: '12px 4px', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', boxSizing: 'border-box' }}>Taxable Value (₹)</div>
          <div style={{ textAlign: 'center', gridColumn: '9 / span 4', padding: '12px 4px', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', boxSizing: 'border-box' }}>Tax payable (₹)</div>
        </div>

        {/* Sub-header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, minmax(80px, 1fr))',
          gap: '8px',
          fontWeight: '600',
          color: '#475569',
          padding: '0',
          fontSize: '12px',
          backgroundColor: '#f8fafc',
          marginBottom: '8px',
          minHeight: '40px',
          alignItems: 'stretch'
        }}>
          <div></div><div></div><div></div>
          <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 4px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>Central Tax</div>
          <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 4px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>State/UT Tax</div>
          <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 4px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>Integrated Tax</div>
          <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 4px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>Cess</div>
          <div></div>
          <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 4px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>Central Tax</div>
          <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 4px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>State/UT Tax</div>
          <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 4px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>Integrated Tax</div>
          <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 4px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>Cess</div>
        </div>

        {/* Table Body */}
        <div style={{
          maxHeight: '500px',
          overflowY: 'auto',
          marginTop: '0px',
          scrollbarWidth: 'thin',
          scrollbarColor: '#64748b #f1f5f9'
        }}>
          <style>{`
            div::-webkit-scrollbar { width: 12px; }
            div::-webkit-scrollbar-track { background: #f1f5f9; borderRadius: 6px; }
            div::-webkit-scrollbar-thumb { background: #64748b; borderRadius: 6px; }
            div::-webkit-scrollbar-thumb:hover { background: #475569; }
          `}</style>

          {data && Array.isArray(data) && data.length > 0 ? (
            data.map((item, index) => {
              const getRowColor = (i) => ['#e0e7ff', '#e9d5ff', '#f3e8ff', '#ede9fe', '#ddd6fe'][i % 5];

              return (
                <div key={index} style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(12, minmax(80px, 1fr))',
                  gap: '8px',
                  alignItems: 'stretch',
                  padding: '0',
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                  marginBottom: '4px',
                  minHeight: '50px'
                }}>
                  <div style={{
                    color: '#1e293b',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'center',
                    padding: '12px 4px',
                    backgroundColor: getRowColor(index),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    minHeight: '50px',
                    boxSizing: 'border-box'
                  }}>
                    {index + 1}
                  </div>

                  <div style={{
                    color: '#334155',
                    fontSize: '12px',
                    padding: '12px 4px',
                    backgroundColor: '#f1f5f9',
                    wordBreak: 'break-word',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    minHeight: '50px',
                    boxSizing: 'border-box'
                  }}>
                    {item.description || 'N/A'}
                  </div>

                  {/* Read-only values */}
                  <div title={item.taxable_non_taxable_value?.toLocaleString() || '0'} style={{
                    color: '#1e293b',
                    fontWeight: '500',
                    fontSize: '14px',
                    fontFamily: '"Roboto Mono", monospace',
                    textAlign: 'center',
                    padding: '8px',
                    backgroundColor: '#ecfdf5',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: '80px',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}>
                    {hiddenColumnsDescriptions.includes(item.description) ? '' : `₹${item.taxable_non_taxable_value?.toLocaleString() || '0'}`}
                  </div>

                  <div title={item.central_tax?.toLocaleString() || '0'} style={{
                    color: '#1e293b',
                    fontWeight: '500',
                    fontSize: '14px',
                    fontFamily: '"Roboto Mono", monospace',
                    textAlign: 'right',
                    padding: '8px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: '80px',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}>
                    ₹{item.central_tax?.toLocaleString() || '0'}
                  </div>

                  <div title={item.state_tax_ut_tax?.toLocaleString() || item.state_ut_tax?.toLocaleString() || '0'} style={{
                    color: '#1e293b',
                    fontWeight: '500',
                    fontSize: '14px',
                    fontFamily: '"Roboto Mono", monospace',
                    textAlign: 'right',
                    padding: '8px',
                    backgroundColor: '#dbeafe',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: '80px',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}>
                    ₹{item.state_tax_ut_tax?.toLocaleString() || item.state_ut_tax?.toLocaleString() || '0'}
                  </div>

                  <div title={item.integrated_tax?.toLocaleString() || '0'} style={{
                    color: '#1e293b',
                    fontWeight: '500',
                    fontSize: '14px',
                    fontFamily: '"Roboto Mono", monospace',
                    textAlign: 'right',
                    padding: '8px',
                    backgroundColor: '#f3e8ff',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: '80px',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}>
                    ₹{item.integrated_tax?.toLocaleString() || '0'}
                  </div>

                  <div title={item.cess?.toLocaleString() || '0'} style={{
                    color: '#1e293b',
                    fontWeight: '500',
                    fontSize: '14px',
                    fontFamily: '"Roboto Mono", monospace',
                    textAlign: 'right',
                    padding: '8px',
                    backgroundColor: '#fce7f3',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: '80px',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}>
                    ₹{item.cess?.toLocaleString() || '0'}
                  </div>

                  {/* Manual Inputs */}
                  <div style={{
                    padding: '8px 4px',
                    backgroundColor: '#ecfdf5',
                    display: 'flex',
                    textAlign: 'right',
                    borderRadius: '6px',
                    minHeight: '50px',
                    boxSizing: 'border-box',
                    minWidth: '80px',
                    maxWidth: '100%',
                  }}>
                    <input
                      type="text"
                      value={getDisplayValue(inputValues[index].taxable_and_non_taxable_manual)}
                      onChange={(e) => handleManualInputChange(index, 'taxable_and_non_taxable_manual', e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={inputStyle}
                      onFocus={(e) => {
                        Object.assign(e.target.style, inputFocusStyle);
                        if (e.target.value) {
                          e.target.setSelectionRange(1, e.target.value.length);
                        }
                      }}
                      onBlur={(e) => Object.assign(e.target.style, inputBlurStyle)}
                    />
                  </div>

                  <div style={{
                    padding: '8px 4px',
                    backgroundColor: '#fef3c7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    minHeight: '50px',
                    boxSizing: 'border-box',
                    minWidth: '80px',
                    maxWidth: '100%',
                  }}>
                    <input
                      type="text"
                      value={getDisplayValue(inputValues[index].central_tax_manual)}
                      onChange={(e) => handleManualInputChange(index, 'central_tax_manual', e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={inputStyle}
                      onFocus={(e) => {
                        Object.assign(e.target.style, inputFocusStyle);
                        if (e.target.value) {
                          e.target.setSelectionRange(1, e.target.value.length);
                        }
                      }}
                      onBlur={(e) => Object.assign(e.target.style, inputBlurStyle)}
                    />
                  </div>

                  <div style={{
                    padding: '8px 4px',
                    backgroundColor: '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    minHeight: '50px',
                    boxSizing: 'border-box',
                    minWidth: '80px',
                    maxWidth: '100%',
                  }}>
                    <input
                      type="text"
                      value={getDisplayValue(inputValues[index].state_tax_ut_tax_manual)}
                      onChange={(e) => handleManualInputChange(index, 'state_tax_ut_tax_manual', e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={inputStyle}
                      onFocus={(e) => {
                        Object.assign(e.target.style, inputFocusStyle);
                        if (e.target.value) {
                          e.target.setSelectionRange(1, e.target.value.length);
                        }
                      }}
                      onBlur={(e) => Object.assign(e.target.style, inputBlurStyle)}
                    />
                  </div>

                  <div style={{
                    padding: '8px 4px',
                    backgroundColor: '#f3e8ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    minHeight: '50px',
                    boxSizing: 'border-box',
                    minWidth: '80px',
                    maxWidth: '100%',
                  }}>
                    <input
                      type="text"
                      value={getDisplayValue(inputValues[index].integrated_tax_manual)}
                      onChange={(e) => handleManualInputChange(index, 'integrated_tax_manual', e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={inputStyle}
                      onFocus={(e) => {
                        Object.assign(e.target.style, inputFocusStyle);
                        if (e.target.value) {
                          e.target.setSelectionRange(1, e.target.value.length);
                        }
                      }}
                      onBlur={(e) => Object.assign(e.target.style, inputBlurStyle)}
                    />
                  </div>

                  <div style={{
                    padding: '8px 4px',
                    backgroundColor: '#fce7f3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    minHeight: '50px',
                    boxSizing: 'border-box',
                    minWidth: '80px',
                    maxWidth: '100%',
                  }}>
                    <input
                      type="text"
                      value={getDisplayValue(inputValues[index].cess_manual)}
                      onChange={(e) => handleManualInputChange(index, 'cess_manual', e.target.value)}
                      onKeyDown={handleKeyDown}
                      style={inputStyle}
                      onFocus={(e) => {
                        Object.assign(e.target.style, inputFocusStyle);
                        if (e.target.value) {
                          e.target.setSelectionRange(1, e.target.value.length);
                        }
                      }}
                      onBlur={(e) => Object.assign(e.target.style, inputBlurStyle)}
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>
              No data available for this section.
            </div>
          )}
        </div>

        {/* Footer */}
        {data && Array.isArray(data) && data.length > 0 && (
          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            borderTop: '2px solid #e2e8f0'
          }}>
            <p style={{ margin: '0', fontSize: '14px', color: '#64748b', textAlign: 'center' }}>
              Total Records: {data.length}
            </p>
          </div>
        )}

        {/* Banner & Submit */}
        <div style={{
          marginTop: '30px',
          display: 'flex',
          alignItems: 'center',
          height: '80px',
          gap: '0'
        }}>
          <div style={{
            position: 'relative',
            width: '50%',
            height: '40px',
            background: 'linear-gradient(135deg, rgb(187, 63, 25) 0%, rgb(157, 58, 58) 50%, rgb(127, 205, 205) 100%)',
            clipPath: 'polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 25px rgba(168, 230, 207, 0.3)',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              right: '10px',
              width: '40px',
              height: '100%',
              background: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'%3E%3Cdefs%3E%3Cpattern id=\'pattern2\' x=\'0\' y=\'0\' width=\'15\' height=\'15\' patternUnits=\'userSpaceOnUse\'%3E%3Ccircle cx=\'7.5\' cy=\'7.5\' r=\'1.5\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100\' height=\'100\' fill=\'url(%23pattern2)\'/%3E%3C/svg%3E")',
              zIndex: 1
            }}></div>
            <div style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'white',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              zIndex: 2,
              position: 'relative',
              marginRight: '20px',
              textAlign: 'center',
              maxWidth: '80%'
            }}>
              10. Reasons for Un - Reconciled Payment of Tax
            </div>
          </div>

          <div style={{
            width: '50%',
            height: '80px',
            display: 'flex',
            alignItems: 'center',
            padding: '0 20px',
            backgroundColor: '#f8fafc',
            border: '2px solid #e2e8f0',
            borderLeft: 'none',
            gap: '10px'
          }}>
            <textarea
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Enter additional notes or remarks..."
              style={{
                flex: 1,
                padding: '10px 15px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Arial, sans-serif',
                backgroundColor: 'white',
                outline: 'none',
                resize: 'none',
                height: '60px'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#88d8a3';
                e.target.style.boxShadow = '0 0 0 2px rgba(136, 216, 163, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
            <button
              onClick={handleSubmit}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #ffd3a5 0%, #fd9853 50%, #e8a87c 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(255, 211, 165, 0.3)',
                transition: 'all 0.2s ease',
                minWidth: '80px'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 16px rgba(255, 211, 165, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(255, 211, 165, 0.3)';
              }}
            >
              Submit
            </button>
          </div>
        </div>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}

export default Nine;